// src/components/CustomerFlow/CustomerFlow.js

import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCreditCard,
  faEye,
  faFileAlt,
  faFileSignature,
  faIdCard,
  faLocationDot,
  faPhone,
  faCamera
} from '@fortawesome/free-solid-svg-icons';
// Importar estilos base de PurchaseFlow
import '../PurchaseFlow/PurchaseFlow.css';
// Importar estilos específicos de CustomerFlow
import './CustomerFlow.css';
import { PageTitleContext } from '../../layouts/MobileLayout';
import { resolveCurrentCustomerFlowStepIndex } from './customerFlowStepResolver';

// Importar todos los componentes de pasosGeoLocationValidationStep
import IdentityValidationStep from './Steps/IdentityValidationStep';
import PaymentProcessingStep from './Steps/PaymentProcessingStep';
import ConfirmationStep from './Steps/ConfirmationStep';
import DocumentUploadStep from './Steps/DocumentUploadStep';
import PhoneValidationStep from './Steps/PhoneValidationStep';
import PreSignatureConfirmationStep from './Steps/PreSignatureConfirmationStep';
import GeoLocationValidationStep from './Steps/OnSiteValidationStep';

import FinalStep from './Steps/FinalStep';
import SaleFlowErrorBoundary from '../SaleFlowErrorBoundary';
import { trackMobileCustomerFlowStepEntered, trackMobileCustomerFlowStepCompleted, trackMobileCustomerFlowCompleted } from '../../analytics/events';
import { safeLocalStorage } from '../../utils/safeLocalStorage';

const STEP_ICON_MAP = {
  ValidaStep: faIdCard,
  PaymentStep: faCreditCard,
  ConfirmationStep: faEye,
  DocumentUploadStep: faCamera,
  PhoneValidationStep: faPhone,
  PreSignatureConfirmationStep: faFileSignature,
  GeoLocationValidationStep: faLocationDot
};

const CustomerFlow = ({ 
  flowData, 
  stepStatuses, 
  timeRemaining, 
  onStepComplete,
  onStepBack,
  searchParams
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [formData, setFormData] = useState({});
  const trackedStepsRef = useRef(new Set()); // Track which steps have been tracked for "entered" event
  const { setPageTitle, setHeaderRightContent } = useContext(PageTitleContext);

  // Mapeo de pasos a componentes
  const stepComponentMap = {
    'ValidaStep': IdentityValidationStep,
    'PaymentStep': PaymentProcessingStep,
    'ConfirmationStep': ConfirmationStep,
    'DocumentUploadStep': DocumentUploadStep,
    'PhoneValidationStep': PhoneValidationStep,
    'PreSignatureConfirmationStep': PreSignatureConfirmationStep,
    'GeoLocationValidationStep': GeoLocationValidationStep
  };

  // Mapeo de pasos a etiquetas legibles
  const stepLabelMap = {
    'ValidaStep': 'Identidad',
    'PaymentStep': 'Medio de Pago',
    'ConfirmationStep': 'Resumen',
    'DocumentUploadStep': 'Documentación',
    'PhoneValidationStep': 'Validación de Teléfono',
    'PreSignatureConfirmationStep': 'Confirmación y firma',
    'GeoLocationValidationStep': 'Confirmación de ubicación'
  };

  useEffect(() => {
    if (flowData?.flow_config && Object.keys(stepStatuses).length > 0) {
      const stepsArray = flowData.flow_config.map(stepName => stepComponentMap[stepName]).filter(Boolean);
      
      // El FinalStep siempre está disponible para todos los flujos (pero no se muestra en el stepper)
      stepsArray.push(FinalStep);
      
      setSteps(stepsArray);
      
      // Determinar el paso actual basado en prioridades
      const targetStepIndex = determineCurrentStepIndex();
      setCurrentStepIndex(targetStepIndex);
      
      // Track step entered when flow initializes (only once per step)
      if (targetStepIndex < stepsArray.length && flowData?.flow_id) {
        const stepName = flowData.flow_config[targetStepIndex];
        if (stepName) {
          const stepKey = `${flowData.flow_id}-${stepName}`;
          if (!trackedStepsRef.current.has(stepKey)) {
            const branchId = safeLocalStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepEntered({
              commerceId: branchId || 'mobile_flow', // Fallback, should be replaced with actual commerceId from API
              branchId: branchId || undefined,
              stepName: stepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepKey);
          }
        }
      }
    }
  }, [flowData, stepStatuses]);

  const flowStepNames = useMemo(() => flowData?.flow_config || [], [flowData?.flow_config]);

  const headerStepper = useMemo(() => {
    if (flowStepNames.length <= 1) {
      return null;
    }

    return (
      <div className="rg-customer-flow-header-stepper" aria-label="Progreso del proceso">
        {flowStepNames.map((stepName, index) => {
          const isCompleted = stepStatuses[stepName] === 'completed' || stepStatuses[stepName] === 'processed';
          const isCurrentStep = index === currentStepIndex && !isCompleted;
          const stepClassName = [
            'rg-customer-flow-header-stepper__item',
            isCurrentStep ? 'rg-customer-flow-header-stepper__item--active' : '',
            isCompleted ? 'rg-customer-flow-header-stepper__item--completed' : '',
          ].filter(Boolean).join(' ');
          const stepIcon = STEP_ICON_MAP[stepName] || faFileAlt;
          const stepLabel = stepLabelMap[stepName] || stepName;

          return (
            <div
              key={stepName}
              className={stepClassName}
              title={stepLabel}
              aria-label={stepLabel}
            >
              <span className="rg-customer-flow-header-stepper__icon-wrap">
                <span className="rg-customer-flow-header-stepper__icon">
                  {isCompleted ? (
                    <FontAwesomeIcon icon={faCheck} />
                  ) : (
                    <FontAwesomeIcon icon={stepIcon} />
                  )}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    );
  }, [currentStepIndex, flowStepNames, stepStatuses]);

  useEffect(() => {
    setPageTitle?.('');
  }, [setPageTitle]);

  useEffect(() => {
    if (!setHeaderRightContent) {
      return undefined;
    }

    setHeaderRightContent(headerStepper);

    return () => {
      setHeaderRightContent(null);
    };
  }, [headerStepper, setHeaderRightContent]);

  const determineCurrentStepIndex = () => resolveCurrentCustomerFlowStepIndex({
    flowConfig: flowData?.flow_config,
    stepStatuses
  });

  const handleNext = (data, stepStatus = null) => {
    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        ...data,
      };
      return updatedData;
    });

    // Todos los estados avanzan al siguiente paso
    if (stepStatus === 'completed' || stepStatus === 'processed' || stepStatus === 'in_progress') {
      // Track step completed only when actually completed (not just in_progress)
      // Use a ref to track which steps have been completed to avoid duplicates
      if (stepStatus === 'completed' || stepStatus === 'processed') {
        const stepName = flowData?.flow_config?.[currentStepIndex];
        if (stepName && flowData?.flow_id) {
          const stepCompletedKey = `${flowData.flow_id}-${stepName}-completed`;
          if (!trackedStepsRef.current.has(stepCompletedKey)) {
            const branchId = safeLocalStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepCompleted({
              commerceId: branchId || 'mobile_flow', // Fallback, should be replaced with actual commerceId
              branchId: branchId || undefined,
              stepName: stepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepCompletedKey);
          }
        }
      }
      
      // Cualquier estado del paso - avanzar al siguiente
      onStepComplete(data);
      
      // Recalcular el paso actual después de completar
      setTimeout(() => {
        const newStepIndex = determineCurrentStepIndex();
        setCurrentStepIndex(newStepIndex);
        
        // Track step entered (only once per step)
        const newStepName = flowData?.flow_config?.[newStepIndex];
        if (newStepName && flowData?.flow_id) {
          const stepKey = `${flowData.flow_id}-${newStepName}`;
          if (!trackedStepsRef.current.has(stepKey)) {
            const branchId = safeLocalStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepEntered({
              commerceId: branchId || 'mobile_flow', // Fallback
              branchId: branchId || undefined,
              stepName: newStepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepKey);
          }
        }
        
        // Track flow completed if we're on FinalStep
        if (newStepIndex === steps.length - 1 && flowData?.flow_id) {
          const branchId = safeLocalStorage.getItem('temp_tokenBranchId');
          trackMobileCustomerFlowCompleted({
            commerceId: branchId || 'mobile_flow', // Fallback
            branchId: branchId || undefined,
            flowId: flowData.flow_id
          });
        }
      }, 100);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      onStepBack();
    }
  };

  const renderStep = () => {
    const CurrentStepComponent = steps[currentStepIndex];

    if (!CurrentStepComponent) {
      return <div>Error al cargar el paso. Por favor, intenta nuevamente.</div>;
    }

    // Props específicas para el paso final
    if (CurrentStepComponent === FinalStep) {
      return (
        <FinalStep
          onComplete={handleNext}
        />
      );
    }

    return (
      <CurrentStepComponent
        flowData={flowData}
        stepStatuses={stepStatuses}
        timeRemaining={timeRemaining}
        onNext={handleNext}
        onBack={handleBack}
        formData={formData}
        currentStepIndex={currentStepIndex}
        totalSteps={steps.length}
        searchParams={searchParams}
      />
    );
  };

  if (!steps.length) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="text-muted">Configurando tu proceso...</p>
      </div>
    );
  }

  return (
    <div className="customer-flow rg-customer-flow notranslate" translate="no">
      {/* Contenedor del paso renderizado con elevación media */}
      <div className="step-content-container">
        <SaleFlowErrorBoundary key={currentStepIndex}>
          {renderStep()}
        </SaleFlowErrorBoundary>
      </div>
    </div>
  );
};

export default CustomerFlow;
